import { SupportedLocales } from '@/composition/i18nStore'
import type { API } from '@strapi/client'
import type { BlocksContent } from 'vue-strapi-blocks-renderer'

// ---------------- Front end data models ------------------------

export const StrapiComponentName = {
  SimpleBlock: 'content.simple-block',
  Blocks: 'content.blocks',
  Profiles: 'content.profiles',
  Resources: 'content.resources',
  PageGroups: 'content.page-group',
} as const
type StrapiComponentType = (typeof StrapiComponentName)[keyof typeof StrapiComponentName]

interface StrapiComponent {
  id: number
  __component: StrapiComponentType
}
interface BasicStrapiFile {
  id: number
  documentId: string // Favour this ID for referencing
  name: string
  hash: string // HASH'd name  e.g. 'families_45a78e4'
  mime: string // e.g. 'image/jpeg'
  url: string //  RELATIVE to /uploads e.g. `/uploads/[HASH][EXTENSION]
  ext: string // EXTENSION   e.g. '.jpg'
  height: number
  width: number
  size: number // kB?
}

export interface StrapiFile extends BasicStrapiFile {
  id: number
  alternativeText?: string
  caption?: string
  documentId: string // Favour this ID for referencing
  previewUrl?: string
  provider: string // e.g. 'local'
  provider_metadata?: unknown
  formats: {
    small?: BasicStrapiFile
    thumbnail?: BasicStrapiFile
  }
}

export interface SamskapetItem extends StrapiComponent {
  reference?: string
  topic?: string
}

export interface ProfileItem extends StrapiComponent {
  name?: string
  address?: string
  role?: string
  type?: 'person' | 'organisation'
  /* location: {
    latitude: number
    longitude: number
  } */
  image?: StrapiFile // URL
}

export interface Link {
  title: string
  url: string
}

// TODO: incomplete
export interface Resource {
  url: string
}

export interface Block extends StrapiComponent {
  blockId?: string
  style: 'flexible' | 'quotation' | 'site-header'
  title?: string
  subtitle?: string
  header?: string
  description?: BlocksContent
  media?: StrapiFile[]
  backgroundColour?: string
  mediaPosition: 'left' | 'right' | 'bottom' | 'top' | 'background'
  links: Link[]
  resources?: Resource[]
}

export interface ResourceItem extends StrapiComponent {
  name?: string
  description?: string
  url?: string
  topic?: string
  category?: string
  files?: StrapiFile[] // This can be null rather than empty array!
  type?: string
  contributor?: string
}

export interface BlockList extends StrapiComponent {
  blockId?: string
  style: 'carousel' | 'cards' | 'grid' | 'flow'
  subStyle: 'none' | 'one' | 'two' | 'three' | 'four' | 'five'
  navigation: boolean
  details: Block
  blocks: Block[]
}
export interface ProfileList extends StrapiComponent {
  type: string
  details: Block
  profiles: ProfileItem[]
}
export interface ResourceList extends StrapiComponent {
  type: string
  details: Block // TODO: Not yet added to Strapi due to a Strapi error
  itemsPerPage?: number
  resources: ResourceItem[]
}
export interface PageGroup extends StrapiComponent {
  details: Block
  pages: Page[]
}

export type Section = Block | BlockList | ProfileList | ResourceList | PageGroup

export interface Page extends API.Document {
  id: string
  locale: string
  name: string
  sections: Section[]
}

/**
 * Classification hierarchy for resources, from broadest to most specific.
 * Resources can contain other resources only if the contained resources
 * have classifications appearing later in this array. So the order of items is important.
 */
export const Classification_List = [
  'topic',
  'theory',
  'method',
  'design',
  'tool',
  'product',
] as const
export type Classification = (typeof Classification_List)[number]

/**
 * Alternative stages for the build option.
 */
export const Alternative_List = ['one', 'two', 'three'] as const
export type Alternative = (typeof Alternative_List)[number]

/**
 * Alternatives for the 'What' build option.
 */
export const Category_List = [
  'what',
  'how',
  'who',
  'where',
  'when',
  'challenges & opportunities',
] as const
export type Category = (typeof Category_List)[number]

/**
 * Additional categories e.g 'video', 'e-book', etc.
 */
export const Subcategory_List = [
  'article',
  'video',
  'tool',
  'e-book',
  'guideline',
  'other',
] as const
export type Subcategory = (typeof Subcategory_List)[number]

/**
 * Base properties shared by Resource and ResourceGroup
 */
interface ResourceBase extends API.Document {
  title?: string
  url: string
  description?: string
  language?: string
  classification: Classification
  alternative?: Alternative // This is only used for the 'What' category
  category?: Category // 'what', 'where', etc.
  subcategory?: Subcategory // 'video', 'pdf', etc
  case: string // relation to a Strapi Page: id of a Page
}

/**
 * Resource interface with hierarchical containment support.
 *
 * Containment rules:
 * - A resource can contain other resources with classifications that appear
 *   LATER in the CLASSIFICATION_ORDER array
 * - Example: 'topic' can contain 'theory', 'method', 'design', 'tool', or 'product'
 * - Example: 'tool' can only contain 'product'
 * - Example: 'product' cannot contain any other resources
 */
export interface Resource extends ResourceBase {
  files?: StrapiFile[] // This can be null rather than empty array!
  // Parent resource that contains this resource (optional, for bidirectional navigation)
  parent: ResourceGroup
}

export interface ResourceGroup extends ResourceBase {
  resources: Resource[]
}

export interface LikeableResource extends Resource {
  liked: boolean
}
export interface LikeableResourceGroup extends ResourceGroup {
  liked: boolean
  resources: LikeableResource[]
}

export interface Project {
  id: string
  title: string
  description: string
  lastEdited: Date
  resources: string[] // List of resource 'documentID'
  whatFreeText: string
}
export interface LikeableProject extends Project {
  liked: boolean
}

export interface CuratedProject extends API.Document {
  id: string
  title: string
  description: string
  resources: {
    id: string
    documentId: string
  }[]
}

export interface User {
  id: string
  projects: Project[]
  likedResources: string[] // Resource 'documentID'
  likedCases: string[] // Page ID?
  createdAt?: string
  updatedAt?: string
}

// ---------------- Samskapet models ------------------------

export interface SamskapetFile {
  filename: string
  mimetype: string
  link: string
  duration: number
}

export interface SamskapetDoc {
  _id: string
  title: string
  description: string
  category: string
  keywords: string[]
  topics: string[]
  authors: string[]
  source: {
    type: string
    files: SamskapetFile[]
  }
}

export interface OramaResult {
  document: {
    samletId: string
    authors: string[]
    title: string
    description: string // Possibly HTML!
    category: string
    keywords: string[]
    topics: string[]
    duration: number
  }
  id: string
  score: number
}

// --------------------------------------------------------------

type Locales = keyof typeof SupportedLocales

export type LC<T> = {
  [key in Locales]: T | null
}

export interface FolderItemSchema {
  id: string
  folderName: string
  parentId?: string
}
